/**
 * Command definitions for the command palette.
 *
 * Commands are organized into groups:
 * - Navigation: Go to pages (g prefix for shortcuts)
 * - Actions: Open modals, trigger operations
 * - Search: Symbol lookup
 */
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePortfolioFilter } from '../../contexts'

export type CommandGroup = 'navigation' | 'actions' | 'search'

export interface Command {
  id: string
  group: CommandGroup
  label: string
  description?: string
  shortcut?: string
  keywords: string[]
  onSelect: () => void
}

export function useCommands(): Command[] {
  const navigate = useNavigate()
  const { portfolios, selectedPortfolioId, selectPortfolio } = usePortfolioFilter()

  return useMemo(() => {
    const commands: Command[] = [
      // Navigation commands (g prefix)
      {
        id: 'nav-dashboard',
        group: 'navigation',
        label: 'Dashboard',
        description: 'Portfolio overview and charts',
        shortcut: 'g d',
        keywords: ['home', 'overview', 'summary', 'portfolio'],
        onSelect: () => navigate('/'),
      },
      {
        id: 'nav-holdings',
        group: 'navigation',
        label: 'Holdings',
        description: 'View and manage your positions',
        shortcut: 'g h',
        keywords: ['positions', 'stocks', 'shares', 'investments'],
        onSelect: () => navigate('/holdings'),
      },
      {
        id: 'nav-watchlist',
        group: 'navigation',
        label: 'Watchlist',
        description: 'Symbols you are tracking',
        shortcut: 'g w',
        keywords: ['watch', 'track', 'monitor', 'following'],
        onSelect: () => navigate('/watchlist'),
      },
      {
        id: 'nav-research',
        group: 'navigation',
        label: 'Research',
        description: 'Deep dive into symbols',
        shortcut: 'g r',
        keywords: ['analyze', 'filings', 'earnings', 'news', 'ai'],
        onSelect: () => navigate('/research'),
      },
      {
        id: 'nav-news',
        group: 'navigation',
        label: 'News',
        description: 'Recent market news and sentiment',
        shortcut: 'g n',
        keywords: ['articles', 'headlines', 'sentiment'],
        onSelect: () => navigate('/news'),
      },
      {
        id: 'nav-analytics',
        group: 'navigation',
        label: 'Analytics',
        description: 'Grafana dashboards',
        shortcut: 'g a',
        keywords: ['charts', 'grafana', 'metrics', 'data'],
        onSelect: () => navigate('/analytics'),
      },
      {
        id: 'nav-portfolios',
        group: 'navigation',
        label: 'Portfolios',
        description: 'Manage portfolio groupings',
        shortcut: 'g p',
        keywords: ['groups', 'collections'],
        onSelect: () => navigate('/portfolios'),
      },
      {
        id: 'nav-theses',
        group: 'navigation',
        label: 'Theses',
        description: 'Investment theses and beliefs',
        shortcut: 'g t',
        keywords: ['thesis', 'strategy', 'beliefs', 'allocations'],
        onSelect: () => navigate('/strategy/theses'),
      },
      {
        id: 'nav-allocations',
        group: 'navigation',
        label: 'Allocations',
        description: 'Thesis allocation drift tracking',
        shortcut: 'g l',
        keywords: ['drift', 'target', 'rebalance', 'weight'],
        onSelect: () => navigate('/strategy/allocations'),
      },
      {
        id: 'nav-rules',
        group: 'navigation',
        label: 'Rules',
        description: 'Alert rules and conditions',
        keywords: ['alerts', 'triggers', 'conditions', 'notifications'],
        onSelect: () => navigate('/strategy/rules'),
      },
      {
        id: 'nav-earnings',
        group: 'navigation',
        label: 'Earnings Calendar',
        description: 'Upcoming earnings dates',
        keywords: ['calendar', 'reports', 'quarterly'],
        onSelect: () => navigate('/strategy/earnings'),
      },
      {
        id: 'nav-philosophy',
        group: 'navigation',
        label: 'Philosophy',
        description: 'Investment philosophy document',
        keywords: ['worldview', 'approach', 'beliefs'],
        onSelect: () => navigate('/strategy/philosophy'),
      },
      {
        id: 'nav-settings',
        group: 'navigation',
        label: 'Settings',
        description: 'Account and preferences',
        shortcut: 'g s',
        keywords: ['profile', 'accounts', 'preferences', 'notifications'],
        onSelect: () => navigate('/settings'),
      },

      // Action commands
      {
        id: 'action-deploy-capital',
        group: 'actions',
        label: 'Deploy Capital',
        description: 'Allocate capital to thesis-aligned investments',
        keywords: ['deploy', 'invest', 'capital', 'allocation', 'buy', 'thesis'],
        onSelect: () => navigate('/strategy/deploy'),
      },
      {
        id: 'action-buy-simulator',
        group: 'actions',
        label: 'Buy Simulator',
        description: 'Simulate a purchase and see impact',
        keywords: ['what-if', 'simulate', 'buy', 'purchase', 'calculator'],
        onSelect: () => navigate('/what-if/buy'),
      },
      {
        id: 'action-position-sizing',
        group: 'actions',
        label: 'Position Sizing',
        description: 'Calculate optimal position size',
        keywords: ['what-if', 'size', 'calculate', 'shares'],
        onSelect: () => navigate('/what-if/position'),
      },
      {
        id: 'action-rebalance',
        group: 'actions',
        label: 'Rebalance Preview',
        description: 'Preview rebalancing transactions',
        keywords: ['what-if', 'rebalance', 'adjust', 'drift'],
        onSelect: () => navigate('/what-if/rebalance'),
      },

      // Portfolio switch commands
      {
        id: 'switch-all-portfolios',
        group: 'actions',
        label: 'View All Portfolios',
        description: 'Show data from all portfolios',
        keywords: ['switch', 'portfolio', 'filter', 'all', 'global'],
        onSelect: () => selectPortfolio(null),
      },
    ]

    // Add dynamic portfolio switch commands
    for (const portfolio of portfolios) {
      // Skip if this portfolio is already selected
      if (portfolio.portfolio_id === selectedPortfolioId) continue

      commands.push({
        id: `switch-portfolio-${portfolio.portfolio_id}`,
        group: 'actions',
        label: `Switch to ${portfolio.name}`,
        description: `Filter views to ${portfolio.name} portfolio`,
        keywords: ['switch', 'portfolio', 'filter', portfolio.name.toLowerCase()],
        onSelect: () => selectPortfolio(portfolio.portfolio_id),
      })
    }

    return commands
  }, [navigate, portfolios, selectedPortfolioId, selectPortfolio])
}

/**
 * Filter commands by search query.
 * Matches against label, description, and keywords.
 */
export function filterCommands(commands: Command[], query: string): Command[] {
  if (!query.trim()) {
    return commands
  }

  const lowerQuery = query.toLowerCase().trim()

  return commands.filter((command) => {
    // Check label
    if (command.label.toLowerCase().includes(lowerQuery)) {
      return true
    }
    // Check description
    if (command.description?.toLowerCase().includes(lowerQuery)) {
      return true
    }
    // Check keywords
    if (command.keywords.some(kw => kw.toLowerCase().includes(lowerQuery))) {
      return true
    }
    return false
  })
}

/**
 * Group commands by their group type.
 */
export function groupCommands(commands: Command[]): Record<CommandGroup, Command[]> {
  const groups: Record<CommandGroup, Command[]> = {
    navigation: [],
    actions: [],
    search: [],
  }

  for (const command of commands) {
    groups[command.group].push(command)
  }

  return groups
}

/**
 * Get display label for a command group.
 */
export function getGroupLabel(group: CommandGroup): string {
  switch (group) {
    case 'navigation':
      return 'Go to'
    case 'actions':
      return 'Actions'
    case 'search':
      return 'Search'
  }
}
